// Lightweight boot-chain timing. On in dev; in prod opt in via ?gv2_perf=1
// (or localStorage 'gv2_perf'='1'), ?gv2_perf=0 to disable. Use the URL param in
// a fresh incognito window for cold-load testing.
// Usage: mark('name') at checkpoints; summary() prints deltas when done.

function isEnabled() {
  // Off under Vitest so the boot-chain table doesn't spam test output. Specs that
  // exercise perf stub VITEST off.
  if (process.env.VITEST) return false
  if (process.env.NODE_ENV !== 'production') return true
  try {
    if (typeof window !== 'undefined' && window.location) {
      const param = new URLSearchParams(window.location.search).get('gv2_perf')
      if (param === '1') {
        try { localStorage.setItem('gv2_perf', '1') } catch { /* ignore */ }
        return true
      }
      if (param === '0') {
        try { localStorage.removeItem('gv2_perf') } catch { /* ignore */ }
        return false
      }
    }
    return localStorage.getItem('gv2_perf') === '1'
  } catch {
    return false
  }
}

const _marks = []
let _summaryPrinted = false

export function mark(name) {
  if (!isEnabled()) return
  const t = performance.now()
  _marks.push({ name, t })
  if (typeof performance.mark === 'function') performance.mark(name)
}

// Seed the pre-app network phase (DNS/TLS/TTFB) from Navigation Timing. Call once at boot, before the first mark.
export function markNavigation() {
  if (!isEnabled()) return
  try {
    const nav = performance.getEntriesByType('navigation')[0]
    if (!nav) return
    const seed = (name, t) => { if (t > 0) _marks.push({ name, t }) }
    _marks.push({ name: 'nav:start', t: 0 })
    seed('nav:dns', nav.domainLookupEnd)
    seed('nav:connect+tls', nav.connectEnd)
    seed('nav:ttfb', nav.responseStart)
    seed('nav:html', nav.responseEnd)
  } catch { /* ignore */ }
}

export function summary(label = 'boot chain') {
  if (!isEnabled() || _summaryPrinted || _marks.length === 0) return
  _summaryPrinted = true

  const first = _marks[0].t
  const rows = _marks.map((m, i) => {
    const fromStart = (m.t - first).toFixed(1) + 'ms'
    const delta = i === 0 ? '—' : '+' + (m.t - _marks[i - 1].t).toFixed(1) + 'ms'
    return { mark: m.name, fromStart, delta }
  })

  console.groupCollapsed(`[perf] ${label} (${(_marks[_marks.length - 1].t - first).toFixed(1)}ms total)`)
  console.table(rows)
  console.groupEnd()
}

export function reset() {
  _marks.length = 0
  _summaryPrinted = false
}

export function enable() {
  try { localStorage.setItem('gv2_perf', '1') } catch { /* ignore */ }
}

export function disable() {
  try { localStorage.removeItem('gv2_perf') } catch { /* ignore */ }
}

// Repeatable click → image-visible timing, separate from the one-shot boot summary()
// above (which only ever prints once per page load). Spans the gallery click and the
// WorkModal mount that follows it, even though they're different route components —
// the sequence lives here as a module-level singleton so both sides can append to it.
let _clickSeq = null

export function startClickToModal() {
  if (!isEnabled()) { _clickSeq = null; return }
  _clickSeq = { t0: performance.now(), marks: [] }
}

export function markClickToModal(name) {
  if (!_clickSeq) return
  _clickSeq.marks.push({ name, t: performance.now() })
}

export function endClickToModal(label = 'gallery click → image visible') {
  if (!_clickSeq) return
  const { t0, marks } = _clickSeq
  _clickSeq = null
  if (!marks.length) return
  const rows = marks.map((m, i) => ({
    mark: m.name,
    fromClick: (m.t - t0).toFixed(1) + 'ms',
    delta: i === 0 ? '—' : '+' + (m.t - marks[i - 1].t).toFixed(1) + 'ms'
  }))
  console.groupCollapsed(`[perf] ${label} (${(marks[marks.length - 1].t - t0).toFixed(1)}ms total)`)
  console.table(rows)
  console.groupEnd()
}
